import { useQueryClient } from "@tanstack/react-query";
import { AnimatePresence } from "framer-motion";
import { forEach, get, isArray, isBoolean, isEmpty } from "lodash";
import { Children, useReducer } from "react";
import { withTranslation } from "react-i18next";

import BankingData from "@krea/customer-features/shared-components/banking-data";
import { Error } from "@krea/customer-features/shared-components/fields/error";
import Fieldset from "@krea/customer-features/shared-components/fields/fieldset";
import FileItem from "@krea/customer-features/shared-components/fields/file-item";
import { FileInput } from "@krea/customer-features/shared-components/fields/file/index";
import FilesList from "@krea/customer-features/shared-components/fields/files-list";
import Radio from "@krea/customer-features/shared-components/fields/radio";
import RadioGroup from "@krea/customer-features/shared-components/fields/radiogroup";
import { useGetCachedCurrentLoanApplication } from "@krea/customer-features/store/current-loan-application/hooks";
import {
  useDeleteFile,
  usePostFieldRequest,
} from "@krea/customer-features/store/field-requests/hooks/mutations";
import { useGetOneFieldRequest } from "@krea/customer-features/store/field-requests/hooks/queries";
import { ONE_FIELD_REQUEST_STORE_KEY } from "@krea/customer-features/store/queryClient";
import { RESOURCE_TYPE } from "@krea/customer-features/utils";

import FieldRequestPreviewHeader from "../FieldRequestPreviewHeader";
import FieldRequestSaved from "../FieldRequestSaved";

const BankingDataForm = ({
  fieldRequestID,
  isPreviewMode,
  t,
  hasParentFieldRequest,
}) => {
  const queryClient = useQueryClient();

  const { data: currentLoanApplication = {} } =
    useGetCachedCurrentLoanApplication();
  const { data: { displayName, submittedData } = {} } =
    useGetOneFieldRequest(fieldRequestID);

  const { mutate: postFieldRequest, error } =
    usePostFieldRequest(fieldRequestID);
  const { mutate: deleteFile } = useDeleteFile();

  const applyingCompanyID = currentLoanApplication?.applyingCompany?.id;
  const files = submittedData?.files || [];

  const selectedAccounts = submittedData?.structured_data;

  const structuredDataExists = submittedData?.has_integration_data;
  const filesExist = files.length > 0;

  // const isWarningShown = !structuredDataExists && !files.length;
  const errors = error?.data?.errors || [];

  function reducer(state, action) {
    switch (action.type) {
      // case "klarnaClientIsLoading":
      //   return { ...state, klarnaClientIsLoading: action.payload };
      case "automationEnabled":
        return { ...state, automationEnabled: action.payload };
      case "isAutomatedSolution":
        return { ...state, isAutomatedSolution: action.payload };
      case "showAutomatedSolutionErrorMessage":
        return { ...state, showAutomatedSolutionErrorMessage: action.payload };
      case "files":
        return { ...state, files: action.payload };
      case "selectedAccounts":
        return { ...state, selectedAccounts: action.payload };
      case "bankingDataSessionError":
        return {
          ...state,
          klarnaClientIsLoading: false,
          automation_enabled: false,
          isAutomatedSolution: false,
          showAutomatedSolutionErrorMessage: true,
        };
      // case "abortKOBConnectError":
      //   return {
      //     ...state,
      //     automation_enabled: false,
      //     isAutomatedSolution: false,
      //     showAutomatedSolutionErrorMessage: true,
      //   };
      case "getBankingSession":
        return { ...state, getBankingSession: action.payload };
      default:
        return state;
    }
  }

  const [state, setState] = useReducer(reducer, {
    // klarnaClientIsLoading: false,
    automationEnabled: true,
    isAutomatedSolution: structuredDataExists || !filesExist,
    showAutomatedSolutionErrorMessage: false,
    files,
    selectedAccounts,
  });

  const {
    automationEnabled,
    isAutomatedSolution,
    showAutomatedSolutionErrorMessage,
    // klarnaClientIsLoading,
  } = state;

  const toggleRadioButton = ({ target }) => {
    const { value } = target;
    const isTrue = value === "true";

    setState({ type: "isAutomatedSolution", payload: isTrue });
  };

  const deleteFileFunc = (fileID) => {
    deleteFile({ fileID, fieldRequestID });
  };

  const createFormData = (value) => {
    const formData = new FormData();

    if (isArray(value.files) && !isEmpty(value.files)) {
      forEach(value.files, (file) => {
        formData.append("files", file, file.name);
      });
    }

    if (isArray(value.structured_data) && !isEmpty(value.structured_data)) {
      formData.append("structured_data", value.structured_data);
    }

    return formData;
  };

  const postFiles = (files) => {
    if (!state.files) {
      setState({ type: "files", payload: files });
    }

    const payload = {
      files: files || state.files,
    };

    if (state.files && payload.files.length) {
      const formData = createFormData(payload);
      postFieldRequest(formData);
    }
  };

  const postSelectedAccounts = (selectedAccounts) => {
    if (!state.selectedAccounts) {
      setState({ type: "selectedAccounts", payload: selectedAccounts });
    }

    const payload = {
      structured_data: selectedAccounts || state.selectedAccounts,
    };

    if (state.selectedAccounts && payload.structured_data.length) {
      postFieldRequest({ structured_data: payload.structured_data });
    }
  };

  const onBlur = () => {
    setState({ type: "showAutomatedSolutionErrorMessage", payload: false });
  };

  const getTitleText = () => {
    return isAutomatedSolution
      ? t("openBankingData.success")
      : t("iaq.uploadedFiles");
  };

  // const renderWarningMessage = (t) => {
  //   return <Warning>{t("iaq.transactionData.shareVia")} Klarna Kosma</Warning>;
  // };

  return (
    <>
      {isPreviewMode ? (
        <>
          <FieldRequestPreviewHeader
            label={displayName}
            value={getTitleText()}
          />

          {submittedData && files.length > 0 && (
            <FieldRequestPreviewHeader
              subheader
              label={
                isBoolean(isAutomatedSolution) &&
                isAutomatedSolution && <>{t("iaq.uploadedFiles")}</>
              }
              value={
                <>
                  {files && !!files.length && (
                    <>
                      <AnimatePresence>
                        {Children.toArray(
                          files.map((file) => (
                            <FileItem
                              file={file}
                              deleteFile={deleteFileFunc}
                              isPreviewMode={isPreviewMode}
                            />
                          )),
                        )}
                      </AnimatePresence>
                    </>
                  )}
                </>
              }
            />
          )}

          {/* {isWarningShown && renderWarningMessage(t)} */}
        </>
      ) : (
        <>
          <form
            className="input-form"
            onBlur={onBlur}
            onSubmit={(e) => e.preventDefault()}
          >
            <Fieldset>
              <Fieldset>
                {!hasParentFieldRequest && automationEnabled && (
                  <RadioGroup
                    name="kob-banking-dataform"
                    value={isAutomatedSolution}
                    disabled={structuredDataExists}
                    onChange={toggleRadioButton}
                  >
                    {/* <div
                      key={"i"}
                      className={twMerge(
                        "tw-border tw-border-solid tw-rounded tw-p-4",
                        { "tw-border-purple-300": isAutomatedSolution },
                      )}
                    > */}
                    <Radio
                      value
                      id="kob-banking-dataform"
                      label={t("iaq.transactionData.automaticFetching")}
                      checked={
                        isBoolean(isAutomatedSolution) && isAutomatedSolution
                      }
                    />

                    {/* {isAutomatedSolution && (
                        <BankingData
                          resourceId={applyingCompanyID}
                          resourceType={RESOURCE_TYPE.APPLYING_COMPANY}
                          onSuccess={async () => {
                            await queryClient.invalidateQueries({
                              queryKey: [
                                ONE_FIELD_REQUEST_STORE_KEY,
                                fieldRequestID,
                              ],
                            });
                          }}
                          isOnboarded={structuredDataExists}
                          className="pt-3"
                          structuredBankData={
                            submittedData ? submittedData.structured_data : null
                          }
                        />
                      )} */}
                    {/* </div> */}

                    <Radio
                      value={false}
                      id="kob-manual-load"
                      label={t("iaq.transactionData.uploadManually")}
                      checked={
                        isBoolean(isAutomatedSolution) && !isAutomatedSolution
                      }
                    />
                  </RadioGroup>
                )}
              </Fieldset>

              {isAutomatedSolution && (
                <BankingData
                  resourceId={applyingCompanyID}
                  resourceType={RESOURCE_TYPE.APPLYING_COMPANY}
                  onSuccess={async () => {
                    await queryClient.invalidateQueries({
                      queryKey: [ONE_FIELD_REQUEST_STORE_KEY, fieldRequestID],
                    });
                  }}
                  isOnboarded={structuredDataExists}
                  className="pt-3"
                  selectedAccounts={selectedAccounts}
                  postSelectedAccounts={postSelectedAccounts}
                />
              )}

              <Fieldset>
                {(!automationEnabled ||
                  (automationEnabled &&
                    isBoolean(isAutomatedSolution) &&
                    !isAutomatedSolution)) && (
                  <FileInput
                    multiple
                    files={files}
                    postFiles={postFiles}
                    error={get(errors, "[0].message", null)}
                  />
                )}

                {files && !!files.length && (
                  <FilesList label={t("iaq.uploadedFiles")}>
                    <AnimatePresence>
                      {Children.toArray(
                        files.map((file) => (
                          <FileItem
                            file={file}
                            deleteFile={deleteFileFunc}
                            isPreviewMode={isPreviewMode}
                          />
                        )),
                      )}
                    </AnimatePresence>
                  </FilesList>
                )}
              </Fieldset>
            </Fieldset>
          </form>

          {!hasParentFieldRequest && (
            <FieldRequestSaved fieldRequestID={fieldRequestID} />
          )}
        </>
      )}

      {errors.map((error, index) => (
        <Error className="mt-3" key={index}>
          {error.message}
        </Error>
      ))}
    </>
  );
};

export default withTranslation()(BankingDataForm);
